/**
 * The one reader of scene-document JSON.
 *
 * `normalizeSceneDocument()` accepts **either** a legacy glXF document or a
 * glTF 2.1 one and returns the same normalized shape. It is deliberately pure
 * — no Babylon import, no I/O, no clock, no randomness — because three
 * consumers depend on that: the loader plugin, the exporter's inverse, and the
 * server-side migration job (handoff WP-E), which must produce byte-identical
 * documents to the ones the viewer writes. (One console warning, for a
 * document that declares the same node `uid` twice, is the single side effect;
 * it changes nothing this returns.)
 *
 * Mapping (plan §1, "glXF → 2.1"):
 *
 *   glXF `assets[i] = { uri }`      →  `files[i] = { uri, mimeType }`
 *   glXF `node.asset = i`           →  `node.file = i`, carrier `externalAsset`
 *   2.1  `externalAssets[i].file=k` →  `node.file = k`, carrier `externalAsset`
 *   2.1  `node.extras.trace.file=k` →  `node.file = k`, carrier `trace`
 *   2.1  behavior `file: k`       →  `src: files[k].uri` (CONTENT_MODEL_SPEC §3.9)
 */

import { mimeTypeFromExtension } from '../../../utils/AssetTypes';
import { isValidUid } from '../../../scenedata/uid';
import { Logger } from '../../../utils/Logger';
import { readNodeAnimationBinding } from './animationBinding/schema';
import { GENERATED_BEHAVIOR_SCHEMAS } from '../../../scenedata/schemas/generated';
import type { FieldSchema } from '../../../scenedata/schemas/types';
import {
    GLTF_BINARY_MIME,
    GLTF_JSON_MIME,
    type IGLTF21,
    type IGLTF21Node,
    type NormalizedFile,
    type NormalizedNode,
    type NormalizedScene,
    type NormalizedSceneDocument,
    type SceneDocumentFormat,
} from './types';
import type { IGLXF } from '../glxf/types';

/** GLB container magic: "glTF" little-endian. */
const GLB_MAGIC = 0x46546c67;
/** GLB chunk type for the JSON chunk. */
const GLB_CHUNK_JSON = 0x4e4f534a;

// =============================================================================
// Reading the bytes
// =============================================================================

/**
 * Parse scene-document JSON out of whatever the loader pipeline has in hand:
 * a JSON string, an already-parsed object, a UTF-8 `ArrayBuffer`, or a GLB
 * container (whose JSON chunk is read).
 *
 * Returns `undefined` rather than throwing when the bytes are not JSON — the
 * content sniff (see {@link isSceneDocument}) needs "not a document" to be a
 * cheap answer, not an exception.
 */
export function parseSceneDocumentJson(data: unknown): Record<string, unknown> | undefined {
    try {
        if (typeof data === 'string') {
            return asObject(JSON.parse(data));
        }
        const bytes = asBytes(data);
        if (bytes) {
            const json = glbJsonChunk(bytes) ?? new TextDecoder().decode(bytes);
            return asObject(JSON.parse(json));
        }
        return asObject(data);
    } catch {
        return undefined;
    }
}

/**
 * Read a candidate scene document out of a `.gltf`/`.glb` blob, cheaply.
 *
 * The sniff runs on every glTF-family open, most of which are ordinary models,
 * so it must not cost a copy of the file:
 *
 *  - a GLB is read header-first and only its JSON chunk is decoded;
 *  - a text glTF is scanned for the two keys that can make it a document
 *    before anything is parsed — a model with embedded base64 buffers is
 *    rejected on a substring search, not a megabyte of `JSON.parse`.
 *
 * The only IO in this module, and platform-generic (`Blob`); the normalizer
 * itself stays pure.
 */
export async function readSceneDocumentJson(blob: Blob): Promise<Record<string, unknown> | undefined> {
    if (blob.size >= 20) {
        const header = new Uint8Array(await blob.slice(0, 20).arrayBuffer());
        const view = new DataView(header.buffer, header.byteOffset, header.byteLength);
        if (view.getUint32(0, true) === GLB_MAGIC) {
            if (view.getUint32(16, true) !== GLB_CHUNK_JSON) return undefined;
            const chunkLength = view.getUint32(12, true);
            return parseSceneDocumentJson(await blob.slice(20, 20 + chunkLength).text());
        }
    }

    const text = await blob.text();
    if (!text.includes('"externalAssets"') && !text.includes('"trace"')) return undefined;
    return parseSceneDocumentJson(text);
}

/**
 * View the input as bytes, or undefined when it is not a buffer at all.
 *
 * `instanceof ArrayBuffer` is false for a buffer that crossed a realm (a
 * worker, a vm context, the test environment), so the duck-typed branch is
 * load-bearing: without it a buffer falls through to the "already parsed
 * object" path and normalizes to an empty document.
 */
function asBytes(data: unknown): Uint8Array | undefined {
    if (data instanceof ArrayBuffer) return new Uint8Array(data);
    if (ArrayBuffer.isView(data)) {
        return new Uint8Array(data.buffer as ArrayBuffer, data.byteOffset, data.byteLength);
    }
    const candidate = data as { byteLength?: unknown } | null;
    if (candidate && typeof candidate === 'object'
        && typeof candidate.byteLength === 'number'
        && !('buffer' in candidate)) {
        try {
            return new Uint8Array(candidate as ArrayBuffer);
        } catch {
            return undefined;
        }
    }
    return undefined;
}

/** The JSON chunk of a GLB container, or undefined when these bytes are not GLB. */
function glbJsonChunk(bytes: Uint8Array): string | undefined {
    if (bytes.byteLength < 20) return undefined;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (view.getUint32(0, true) !== GLB_MAGIC) return undefined;
    const chunkLength = view.getUint32(12, true);
    if (view.getUint32(16, true) !== GLB_CHUNK_JSON) return undefined;
    const end = Math.min(20 + chunkLength, bytes.byteLength);
    return new TextDecoder().decode(bytes.subarray(20, end));
}

function asObject(value: unknown): Record<string, unknown> | undefined {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : undefined;
}

// =============================================================================
// The content sniff
// =============================================================================

/**
 * Whether a parsed glTF-family document is a **Trace scene document** rather
 * than a plain model.
 *
 * `.gltf` and `.glb` name both, so `TraceViewer.open` cannot route on the
 * extension (plan D2). The two signals are structural:
 *
 *  - `externalAssets` — the node is composed of other files, which only a
 *    composition document does; and
 *  - document- or scene-level `extras.trace` — the Trace root payload.
 *
 * Node-level `extras.trace` deliberately does **not** count: a baked GLB asset
 * can carry Trace extras on its nodes and is still a single asset.
 *
 * Legacy glXF answers true through `asset.experience` / its `assets[]` array.
 */
export function isSceneDocument(json: unknown): boolean {
    const doc = asObject(json);
    if (!doc) return false;

    if (Array.isArray(doc.externalAssets) && doc.externalAssets.length > 0) return true;
    if (traceExtras(doc.extras)) return true;

    const scenes = Array.isArray(doc.scenes) ? doc.scenes : [];
    for (const scene of scenes) {
        if (traceExtras(asObject(scene)?.extras)) return true;
    }

    // Legacy glXF: no `files`/`externalAssets`, but an `assets[]` array of
    // external references and (usually) `asset.experience`.
    const asset = asObject(doc.asset);
    if (asset?.experience === true) return true;
    if (Array.isArray(doc.assets) && doc.assets.length > 0 && Array.isArray(doc.nodes)) return true;

    return false;
}

function traceExtras(extras: unknown): boolean {
    return !!asObject(asObject(extras)?.trace);
}

// =============================================================================
// Normalization
// =============================================================================

/**
 * Normalize a legacy glXF or glTF 2.1 scene document.
 *
 * Order is preserved everywhere (files, nodes, scenes) so the transform is
 * deterministic and reversible.
 *
 * @param json - the parsed document, or the bytes/string it came from.
 * @throws if `json` is not an object at all.
 */
export function normalizeSceneDocument(json: unknown): NormalizedSceneDocument {
    const doc = parseSceneDocumentJson(json);
    if (!doc) {
        throw new Error('[normalizeSceneDocument] not a JSON object');
    }

    // Which wire format this is. `files`/`externalAssets` are the usual tell —
    // but a 2.1 document with neither is still 2.1, and the writer deletes both
    // arrays when they come out empty (a scene of primitives and text names no
    // file at all). Read as glXF, such a document lost every 2.1-only field on
    // the way in: node `uid` above all, which is what `Provenance.placedUids`
    // is keyed by (issues #837, #838), so a save → reopen left a copy's link to
    // its asset pointing at identities the reopened nodes no longer had. The
    // version the document declares about itself settles it; glXF declares
    // 2.0.
    const declaredVersion = asObject(doc.asset)?.version;
    const sourceFormat: SceneDocumentFormat = Array.isArray(doc.files)
        || Array.isArray(doc.externalAssets)
        || (typeof declaredVersion === 'string' && declaredVersion.startsWith('2.1'))
        ? 'gltf21'
        : 'glxf';

    return sourceFormat === 'gltf21'
        ? normalizeGltf21(doc as unknown as IGLTF21)
        : normalizeGlxf(doc as unknown as IGLXF);
}

function normalizeGltf21(doc: IGLTF21): NormalizedSceneDocument {
    const files: NormalizedFile[] = (doc.files ?? []).map(file => ({
        uri: file.uri ?? '',
        mimeType: file.mimeType ?? inferMimeType(file.uri ?? ''),
        // Informational, never resolved by (spec §2.1). Carried so a load →
        // save round trip preserves it and an editor can label the node.
        ...(typeof file.name === 'string' && file.name !== '' ? { name: file.name } : {}),
    }));
    const externalAssets = doc.externalAssets ?? [];

    const nodes: NormalizedNode[] = (doc.nodes ?? []).map((node, index) => {
        const normalized = baseNode(node, index);
        resolveBehaviorFileRefs(normalized, files, index);

        // `externalAsset` indexes `externalAssets`, which indexes `files`.
        if (typeof node.externalAsset === 'number') {
            const fileIndex = externalAssets[node.externalAsset]?.file;
            if (typeof fileIndex === 'number' && files[fileIndex]) {
                normalized.file = fileIndex;
                normalized.carrier = 'externalAsset';
            }
        }

        // TRACE_animation_binding: resolve each source's `externalAsset` to the
        // file it names, the way `node.externalAsset` is resolved above.
        const binding = readNodeAnimationBinding(node.extensions);
        if (binding) {
            const problems = [...binding.problems];
            const sources = (binding.sources ?? []).flatMap((source, i) => {
                const fileIndex = externalAssets[source.externalAsset]?.file;
                if (typeof fileIndex !== 'number' || !files[fileIndex]) {
                    problems.push(`sources[${i}].externalAsset ${source.externalAsset} names no file`);
                    return [];
                }
                return [{ file: fileIndex, ...(source.root !== undefined ? { root: source.root } : {}) }];
            });
            if (sources.length === 0) problems.push('no source binds');
            else normalized.animationBinding = { sources, problems };
            if (problems.length > 0) {
                Logger.warn(`[normalizeSceneDocument] node ${index} TRACE_animation_binding: ${problems.join('; ')}`);
            }
        }

        // The Trace carrier for the things 2.1 forbids as external assets:
        // images, video, audio, nested Trace scenes (plan D3).
        if (normalized.file === undefined) {
            const fileIndex = traceFileIndex(normalized.extras);
            if (fileIndex !== undefined && files[fileIndex]) {
                normalized.file = fileIndex;
                normalized.carrier = 'trace';
            }
        }

        return normalized;
    });

    assignNodeUids(doc.nodes ?? [], nodes);

    return {
        sourceFormat: 'gltf21',
        files,
        nodes,
        ...sceneFields(doc.scene, doc.scenes),
        ...documentFields(doc),
    };
}

/**
 * Carry glTF 2.1 node identity onto the normalized nodes (issue #837,
 * SCENE_ROOT_SPEC §2.3 "Identity").
 *
 * Three rules, all 2.1's own:
 *
 * - an explicit `node.uid` is the identity, verbatim;
 * - a node WITHOUT one falls back to its `name`, but only when that name could
 *   have been a uid in the first place: alphanumeric, and unique across every
 *   `uid` and `name` in the document. A duplicated name, or one with a space
 *   or a dot in it, identifies nothing — such a node simply has no uid until
 *   one is minted for it in the session;
 * - **an identity is claimed once.** 2.1 requires uniqueness across the file,
 *   and a hand-edited or third-party document can still declare the same `uid`
 *   on two nodes. The first node in document order keeps it; a later claimant
 *   is read as having none (and is minted one in a 2.1 session), with a
 *   warning naming both. Two live objects sharing one identity is the thing
 *   nothing downstream could recover from — `placedUids`, an override target
 *   and the writer's own uniqueness check all assume it cannot happen — so it
 *   is resolved here, where the document is read, rather than allowed in and
 *   refused at the save that would have written it back.
 *
 * Uniqueness is judged over the document's NODES, which is the only thing
 * anything here addresses. The rule is a READ rule: renaming a node later
 * never changes the uid it was read with.
 *
 * glXF documents never reach this — 2.1 identity does not exist there, and a
 * legacy document is never stamped.
 */
function assignNodeUids(source: readonly IGLTF21Node[], nodes: NormalizedNode[]): void {
    const declared = new Set<string>();
    const nameCount = new Map<string, number>();
    for (const node of source) {
        if (typeof node.uid === 'string' && node.uid !== '') declared.add(node.uid);
        if (typeof node.name === 'string' && node.name !== '') {
            nameCount.set(node.name, (nameCount.get(node.name) ?? 0) + 1);
        }
    }

    /** Identity → the node that took it, so a second claimant can be named. */
    const claimedBy = new Map<string, string>();
    const label = (index: number): string => `"${nodes[index]?.name ?? `node ${index}`}" (node ${index})`;

    for (let i = 0; i < nodes.length; i++) {
        const raw = source[i];
        const uid = typeof raw?.uid === 'string' && raw.uid !== '' ? raw.uid : undefined;
        if (uid) {
            const owner = claimedBy.get(uid);
            if (owner !== undefined) {
                Logger.warn(
                    `[normalizeSceneDocument] duplicate node uid "${uid}": ${owner} keeps it, `
                    + `${label(i)} is read as having none and will be minted a new one`,
                );
                continue;
            }
            nodes[i].uid = uid;
            claimedBy.set(uid, label(i));
            continue;
        }
        const name = typeof raw?.name === 'string' ? raw.name : '';
        if (name && isValidUid(name) && nameCount.get(name) === 1
            && !declared.has(name) && !claimedBy.has(name)) {
            nodes[i].uid = name;
            claimedBy.set(name, label(i));
        }
    }
}

function normalizeGlxf(doc: IGLXF): NormalizedSceneDocument {
    // glXF has no manifest: `assets[i].uri` is the whole dependency list and
    // carries no mime, so infer one from the extension.
    const files: NormalizedFile[] = (doc.assets ?? []).map(asset => ({
        uri: asset.uri ?? '',
        mimeType: inferMimeType(asset.uri ?? ''),
    }));

    const nodes: NormalizedNode[] = (doc.nodes ?? []).map((node, index) => {
        const normalized = baseNode(node, index);
        if (typeof node.asset === 'number' && files[node.asset]) {
            normalized.file = node.asset;
            normalized.carrier = 'externalAsset';
        }
        return normalized;
    });

    return {
        sourceFormat: 'glxf',
        files,
        nodes,
        ...sceneFields(doc.scene, doc.scenes),
        ...documentFields(doc),
    };
}

/** Fields shared by both wire formats: name, transform, children, extras. */
function baseNode(
    node: {
        name?: string;
        children?: number[];
        translation?: number[];
        rotation?: number[];
        scale?: number[];
        matrix?: number[];
        extras?: unknown;
        extensions?: unknown;
    },
    index: number
): NormalizedNode {
    const normalized: NormalizedNode = { name: node.name || `Node_${index}` };
    if (node.children) normalized.children = node.children;
    if (node.translation) normalized.translation = node.translation;
    if (node.rotation) normalized.rotation = node.rotation;
    if (node.scale) normalized.scale = node.scale;
    if (node.matrix) normalized.matrix = node.matrix;
    const extras = asObject(node.extras);
    if (extras) normalized.extras = extras;
    // Never read here — carried so the writer can put it back (issue #834).
    const extensions = asObject(node.extensions);
    if (extensions) normalized.extensions = extensions;
    return normalized;
}

function sceneFields(
    scene: number | undefined,
    scenes: Array<{ nodes?: number[]; name?: string; extras?: unknown }> | undefined
): Pick<NormalizedSceneDocument, 'scene' | 'scenes'> {
    const normalized: NormalizedScene[] = (scenes ?? []).map(s => {
        const out: NormalizedScene = { nodes: s.nodes ?? [] };
        if (s.name !== undefined) out.name = s.name;
        const extras = asObject(s.extras);
        if (extras) out.extras = extras;
        return out;
    });
    return { scene: scene ?? 0, scenes: normalized };
}

type DocumentFields = Pick<
    NormalizedSceneDocument,
    'extensions' | 'extras' | 'extensionsUsed' | 'extensionsRequired' | 'assetExtras'
>;

/**
 * The document's own top-level fields.
 *
 * `extensionsUsed` / `extensionsRequired` / `asset.extras` are carried for one
 * reason only: the writer re-serialises the whole document, so a declaration it
 * does not carry is a declaration it deletes (issue #834, spec §3.6).
 */
function documentFields(doc: {
    extensions?: Record<string, unknown>;
    extras?: unknown;
    extensionsUsed?: string[];
    extensionsRequired?: string[];
    asset?: { extras?: unknown };
}): DocumentFields {
    const out: DocumentFields = {};
    if (doc.extensions) out.extensions = doc.extensions;
    const extrasObject = asObject(doc.extras);
    if (extrasObject) out.extras = extrasObject;
    if (Array.isArray(doc.extensionsUsed)) out.extensionsUsed = doc.extensionsUsed;
    if (Array.isArray(doc.extensionsRequired)) out.extensionsRequired = doc.extensionsRequired;
    const assetExtras = asObject(doc.asset?.extras);
    if (assetExtras) out.assetExtras = assetExtras;
    return out;
}

/**
 * Where a behavior names a file by `src`, and so may name it by `file`
 * instead (CONTENT_MODEL_SPEC §3.9): per `@type`, the paths from the
 * behavior to each object whose schema declares `src`; `[]` is every item of
 * an array. Derived from the generated behavior schemas, so a new `src` field
 * is covered the moment its schema is.
 */
export const BEHAVIOR_SRC_POSITIONS: ReadonlyMap<string, ReadonlyArray<readonly string[]>> = (() => {
    const positions = new Map<string, string[][]>();
    const walk = (type: string, fields: readonly FieldSchema[], path: string[]): void => {
        for (const field of fields) {
            if (field.key === 'src') positions.set(type, [...(positions.get(type) ?? []), path]);
            if (field.type === 'object' && !field.isMap) walk(type, field.fields, [...path, field.key]);
            if (field.type === 'array' && field.itemFields) walk(type, field.itemFields, [...path, field.key, '[]']);
        }
    };
    for (const schema of GENERATED_BEHAVIOR_SCHEMAS) walk(schema.type, schema.fields, []);
    return positions;
})();

/**
 * Resolve `file: k` into `src: files[k].uri` at each position of
 * {@link BEHAVIOR_SRC_POSITIONS}, and nowhere else, so user-keyed data (a
 * state named `file`, a Properties value) is never read as a reference. Like
 * a glTF image's `uri` and `bufferView`, an object carries `src` or `file`,
 * never both; both is invalid and the path wins. An index naming no file
 * reads as `src: null` with a warning: kept, a later save's rebuilt
 * `files[]` could make it name an unrelated file. Pure.
 */
function resolveBehaviorFileRefs(node: NormalizedNode, files: readonly NormalizedFile[], index: number): void {
    const trace = asObject(node.extras?.trace);
    const source = trace?.behaviors;
    if (!trace || !Array.isArray(source)) return;
    const warn = (message: string) => Logger.warn(`[normalizeSceneDocument] node ${index}: ${message}`);

    const fileToSrc = (value: unknown): unknown => {
        const record = asObject(value);
        if (!record || !('file' in record)) return value;
        const { file: k, ...rest } = record;
        if ('src' in record) {
            warn(`a behavior carries both src and file ${JSON.stringify(k)}; file ignored`);
            return rest;
        }
        const entry = typeof k === 'number' && Number.isInteger(k) ? files[k] : undefined;
        if (!entry?.uri) warn(`behavior file ${JSON.stringify(k)} ${entry ? 'has no uri' : 'names no file'}; src read as null`);
        return { ...rest, src: entry?.uri || null };
    };
    // `value` with `fn` applied at `path`; the same object when nothing changed.
    const atPath = (value: unknown, path: readonly string[], fn: (v: unknown) => unknown): unknown => {
        if (path.length === 0) return fn(value);
        const [head, ...tail] = path;
        if (head === '[]') {
            if (!Array.isArray(value)) return value;
            const next = value.map(item => atPath(item, tail, fn));
            return next.some((item, i) => item !== value[i]) ? next : value;
        }
        const record = asObject(value);
        if (!record || !(head in record)) return value;
        const child = atPath(record[head], tail, fn);
        return child === record[head] ? value : { ...record, [head]: child };
    };

    const behaviors = source.map((behavior: unknown) => {
        const type = asObject(behavior)?.['@type'];
        const positions = typeof type === 'string' ? BEHAVIOR_SRC_POSITIONS.get(type) : undefined;
        return (positions ?? []).reduce((b, path) => atPath(b, path, fileToSrc), behavior);
    });
    if (behaviors.some((b, i) => b !== source[i])) {
        node.extras = { ...node.extras, trace: { ...trace, behaviors } };
    }
}

/** `node.extras.trace.file`, when it is a valid index. */
function traceFileIndex(extras: Record<string, unknown> | undefined): number | undefined {
    const file = asObject(extras?.trace)?.file;
    return typeof file === 'number' && Number.isInteger(file) && file >= 0 ? file : undefined;
}

/**
 * Mime type implied by a URI's extension. Used only for legacy glXF, whose
 * `assets[]` entries carry none, and for a 2.1 entry that omits the (spec-
 * required) `mimeType`.
 *
 * `.gltf`/`.glb` get their **registered** types — `mimeTypeFromExtension`
 * still answers `application/gltf+json`, which no policy keys on.
 */
export function inferMimeType(uri: string): string {
    const ext = fileExtensionFromUri(uri);
    if (ext === 'gltf') return GLTF_JSON_MIME;
    if (ext === 'glb') return GLTF_BINARY_MIME;
    return mimeTypeFromExtension(ext) ?? '';
}

/**
 * Extension of a URI's last path segment, lowercased, without the dot.
 *
 * The same rule as `Utility.getFileExtensionFromSrc`, restated here rather
 * than imported: this module is deliberately Babylon-free (see the file
 * header) and `Utility` is not. Both split on the path first, so
 * `https://api.example.com/assets/<guid>/file` answers `''` rather than
 * inheriting the dot from the host name. `utils/file-extension.test.ts` pins
 * the two to the same answers.
 */
export function fileExtensionFromUri(uri: string): string {
    const path = uri.split(/[?#]/)[0];
    const segment = path.substring(path.lastIndexOf('/') + 1);
    const dot = segment.lastIndexOf('.');
    return dot > 0 ? segment.substring(dot + 1).toLowerCase() : '';
}
